import { Sector } from "./types";
import { Translations } from "model/messageModel";
import { getTranslation } from "common";

export type RoiLanguage = "it" | "en" | "de" | "es" | "fr" | "pt";

const en = {
  navigationLabel: "ROI Calculator",
  badge: "Flowenti ROI Calculator",
  heading: "Calculate the return on your automated processes",
  introduction: "Estimate yearly savings, hours recovered, and the payback period in a few steps using process benchmarks.",
  calculatorTitle: "ROI Calculator",
  calculatorIntroduction: "Complete the steps to get an immediate estimate.",
  steps: ["Profile", "Current situation", "Results"],
  sector: "Sector",
  sectors: {
    manifatturiero: "Manufacturing",
    sanita: "Healthcare",
    legale: "Legal / Professional services",
    altro: "Other",
  } as Record<Sector, string>,
  employees: "Employees involved in processes",
  employeesUnit: "people",
  processes: "Expected active processes",
  processesUnit: "active processes",
  weeklyHours: "Total weekly hours spent on manual tasks",
  weeklyHoursUnit: "h/week",
  hourlyCost: "Average gross hourly cost to the company",
  hourlyCostUnit: "€/h",
  monthlyErrors: "Errors or rework per month",
  monthlyErrorsUnit: "events",
  summarySector: "Sector",
  summaryEmployees: "Employees",
  summaryProcesses: "Active processes",
  summaryHours: "Total manual hours",
  summaryCost: "Hourly cost",
  summaryErrors: "Errors",
  perMonth: "per month",
  yearlySavings: "Estimated yearly savings",
  recoveredHours: "Hours recovered/year",
  payback: "Payback period",
  month: "month",
  months: "months",
  netRoi: "Net ROI",
  timeSavings: "Time savings",
  errorSavings: "Error savings",
  flowentiCostPrefix: "Flowenti cost",
  upTo: "up to",
  note: "Flowenti cost is based on the subscription needed for the indicated number of processes. Benefits are indicative benchmarks.",
  back: "Back",
  next: "Next",
  calculate: "Calculate ROI",
  requestDemo: "Request a demo",
};

export type RoiCopy = typeof en;

const translations: Record<RoiLanguage, RoiCopy> = {
  en,
  it: {
    navigationLabel: "Calcolatore ROI",
    badge: "Calcolatore ROI Flowenti",
    heading: "Calcola il ritorno dei tuoi processi automatizzati",
    introduction: "Stima in pochi passaggi il risparmio annuo, le ore recuperate e il periodo di ammortamento usando benchmark di processo.",
    calculatorTitle: "Calcolatore ROI",
    calculatorIntroduction: "Completa i passaggi per ottenere una stima immediata.",
    steps: ["Profilo", "Situazione attuale", "Risultati"],
    sector: "Settore",
    sectors: { manifatturiero: "Manifatturiero", sanita: "Sanità", legale: "Legale / Servizi professionali", altro: "Altro" },
    employees: "Dipendenti coinvolti nei processi",
    employeesUnit: "persone",
    processes: "Processi attivi previsti",
    processesUnit: "processi attivi",
    weeklyHours: "Ore settimanali totali dedicate ad attività manuali",
    weeklyHoursUnit: "h/sett",
    hourlyCost: "Costo orario medio lordo per l'azienda",
    hourlyCostUnit: "€/h",
    monthlyErrors: "Errori o rilavorazioni al mese",
    monthlyErrorsUnit: "eventi",
    summarySector: "Settore",
    summaryEmployees: "Dipendenti",
    summaryProcesses: "Processi attivi",
    summaryHours: "Ore manuali totali",
    summaryCost: "Costo orario",
    summaryErrors: "Errori",
    perMonth: "al mese",
    yearlySavings: "Risparmio annuo stimato",
    recoveredHours: "Ore recuperate/anno",
    payback: "Periodo di ammortamento",
    month: "mese",
    months: "mesi",
    netRoi: "ROI netto",
    timeSavings: "Risparmio sul tempo",
    errorSavings: "Risparmio sugli errori",
    flowentiCostPrefix: "Costo Flowenti",
    upTo: "fino a",
    note: "Il costo Flowenti è calcolato in base all'abbonamento necessario per il numero di processi indicato. I benefici sono stime indicative basate su benchmark.",
    back: "Indietro",
    next: "Avanti",
    calculate: "Calcola ROI",
    requestDemo: "Richiedi una demo",
  },
  de: {
    navigationLabel: "ROI-Rechner",
    badge: "Flowenti ROI-Rechner",
    heading: "Berechnen Sie die Rendite Ihrer automatisierten Prozesse",
    introduction: "Schätzen Sie in wenigen Schritten jährliche Einsparungen, gewonnene Stunden und die Amortisationszeit anhand von Prozessbenchmarks.",
    calculatorTitle: "ROI-Rechner",
    calculatorIntroduction: "Vervollständigen Sie die Schritte für eine sofortige Schätzung.",
    steps: ["Profil", "Aktuelle Situation", "Ergebnisse"],
    sector: "Branche",
    sectors: { manifatturiero: "Fertigung", sanita: "Gesundheitswesen", legale: "Recht / Professionelle Dienstleistungen", altro: "Sonstige" },
    employees: "An Prozessen beteiligte Mitarbeitende",
    employeesUnit: "Personen",
    processes: "Geplante aktive Prozesse",
    processesUnit: "aktive Prozesse",
    weeklyHours: "Gesamte Wochenstunden für manuelle Aufgaben",
    weeklyHoursUnit: "Std./Woche",
    hourlyCost: "Durchschnittliche Bruttostundenkosten für das Unternehmen",
    hourlyCostUnit: "€/Std.",
    monthlyErrors: "Fehler oder Nacharbeit pro Monat",
    monthlyErrorsUnit: "Vorfälle",
    summarySector: "Branche",
    summaryEmployees: "Mitarbeitende",
    summaryProcesses: "Aktive Prozesse",
    summaryHours: "Manuelle Stunden gesamt",
    summaryCost: "Stundenkosten",
    summaryErrors: "Fehler",
    perMonth: "pro Monat",
    yearlySavings: "Geschätzte jährliche Einsparungen",
    recoveredHours: "Gewonnene Stunden/Jahr",
    payback: "Amortisationszeit",
    month: "Monat",
    months: "Monate",
    netRoi: "Netto-ROI",
    timeSavings: "Zeitersparnis",
    errorSavings: "Einsparungen durch weniger Fehler",
    flowentiCostPrefix: "Flowenti-Kosten",
    upTo: "bis zu",
    note: "Die Flowenti-Kosten basieren auf dem für die angegebene Prozesszahl benötigten Abonnement. Die Vorteile sind unverbindliche Benchmark-Schätzungen.",
    back: "Zurück",
    next: "Weiter",
    calculate: "ROI berechnen",
    requestDemo: "Demo anfragen",
  },
  es: {
    navigationLabel: "Calculadora de ROI",
    badge: "Calculadora de ROI de Flowenti",
    heading: "Calcula el retorno de tus procesos automatizados",
    introduction: "Estima en pocos pasos el ahorro anual, las horas recuperadas y el plazo de amortización con referencias de procesos.",
    calculatorTitle: "Calculadora de ROI",
    calculatorIntroduction: "Completa los pasos para obtener una estimación inmediata.",
    steps: ["Perfil", "Situación actual", "Resultados"],
    sector: "Sector",
    sectors: { manifatturiero: "Industria", sanita: "Sanidad", legale: "Servicios jurídicos / Profesionales", altro: "Otros" },
    employees: "Empleados implicados en los procesos",
    employeesUnit: "personas",
    processes: "Procesos activos previstos",
    processesUnit: "procesos activos",
    weeklyHours: "Horas semanales totales dedicadas a tareas manuales",
    weeklyHoursUnit: "h/semana",
    hourlyCost: "Coste bruto medio por hora para la empresa",
    hourlyCostUnit: "€/h",
    monthlyErrors: "Errores o retrabajos al mes",
    monthlyErrorsUnit: "incidencias",
    summarySector: "Sector",
    summaryEmployees: "Empleados",
    summaryProcesses: "Procesos activos",
    summaryHours: "Horas manuales totales",
    summaryCost: "Coste por hora",
    summaryErrors: "Errores",
    perMonth: "al mes",
    yearlySavings: "Ahorro anual estimado",
    recoveredHours: "Horas recuperadas/año",
    payback: "Plazo de amortización",
    month: "mes",
    months: "meses",
    netRoi: "ROI neto",
    timeSavings: "Ahorro de tiempo",
    errorSavings: "Ahorro por reducción de errores",
    flowentiCostPrefix: "Coste de Flowenti",
    upTo: "hasta",
    note: "El coste de Flowenti se calcula según la suscripción necesaria para el número de procesos indicado. Los beneficios son estimaciones orientativas basadas en referencias.",
    back: "Atrás",
    next: "Siguiente",
    calculate: "Calcular ROI",
    requestDemo: "Solicitar una demo",
  },
  fr: {
    navigationLabel: "Calculateur de ROI",
    badge: "Calculateur de ROI Flowenti",
    heading: "Calculez le retour de vos processus automatisés",
    introduction: "Estimez en quelques étapes les économies annuelles, les heures récupérées et le délai d'amortissement à partir de références de processus.",
    calculatorTitle: "Calculateur de ROI",
    calculatorIntroduction: "Suivez les étapes pour obtenir une estimation immédiate.",
    steps: ["Profil", "Situation actuelle", "Résultats"],
    sector: "Secteur",
    sectors: { manifatturiero: "Industrie", sanita: "Santé", legale: "Juridique / Services professionnels", altro: "Autre" },
    employees: "Employés impliqués dans les processus",
    employeesUnit: "personnes",
    processes: "Processus actifs prévus",
    processesUnit: "processus actifs",
    weeklyHours: "Total des heures hebdomadaires consacrées aux tâches manuelles",
    weeklyHoursUnit: "h/semaine",
    hourlyCost: "Coût horaire brut moyen pour l'entreprise",
    hourlyCostUnit: "€/h",
    monthlyErrors: "Erreurs ou reprises par mois",
    monthlyErrorsUnit: "événements",
    summarySector: "Secteur",
    summaryEmployees: "Employés",
    summaryProcesses: "Processus actifs",
    summaryHours: "Heures manuelles au total",
    summaryCost: "Coût horaire",
    summaryErrors: "Erreurs",
    perMonth: "par mois",
    yearlySavings: "Économies annuelles estimées",
    recoveredHours: "Heures récupérées/an",
    payback: "Délai d'amortissement",
    month: "mois",
    months: "mois",
    netRoi: "ROI net",
    timeSavings: "Économies de temps",
    errorSavings: "Économies liées aux erreurs",
    flowentiCostPrefix: "Coût de Flowenti",
    upTo: "jusqu'à",
    note: "Le coût de Flowenti dépend de l'abonnement nécessaire pour le nombre de processus indiqué. Les bénéfices sont des estimations indicatives fondées sur des références.",
    back: "Retour",
    next: "Suivant",
    calculate: "Calculer le ROI",
    requestDemo: "Demander une démo",
  },
  pt: {
    navigationLabel: "Calculadora de ROI",
    badge: "Calculadora de ROI Flowenti",
    heading: "Calcule o retorno dos seus processos automatizados",
    introduction: "Estime em poucos passos a poupança anual, as horas recuperadas e o prazo de recuperação do investimento com referências de processos.",
    calculatorTitle: "Calculadora de ROI",
    calculatorIntroduction: "Conclua os passos para obter uma estimativa imediata.",
    steps: ["Perfil", "Situação atual", "Resultados"],
    sector: "Setor",
    sectors: { manifatturiero: "Indústria", sanita: "Saúde", legale: "Jurídico / Serviços profissionais", altro: "Outro" },
    employees: "Colaboradores envolvidos nos processos",
    employeesUnit: "pessoas",
    processes: "Processos ativos previstos",
    processesUnit: "processos ativos",
    weeklyHours: "Total de horas semanais dedicadas a tarefas manuais",
    weeklyHoursUnit: "h/semana",
    hourlyCost: "Custo horário bruto médio para a empresa",
    hourlyCostUnit: "€/h",
    monthlyErrors: "Erros ou retrabalho por mês",
    monthlyErrorsUnit: "ocorrências",
    summarySector: "Setor",
    summaryEmployees: "Colaboradores",
    summaryProcesses: "Processos ativos",
    summaryHours: "Horas manuais totais",
    summaryCost: "Custo horário",
    summaryErrors: "Erros",
    perMonth: "por mês",
    yearlySavings: "Poupança anual estimada",
    recoveredHours: "Horas recuperadas/ano",
    payback: "Prazo de recuperação",
    month: "mês",
    months: "meses",
    netRoi: "ROI líquido",
    timeSavings: "Poupança de tempo",
    errorSavings: "Poupança com menos erros",
    flowentiCostPrefix: "Custo Flowenti",
    upTo: "até",
    note: "O custo Flowenti baseia-se na subscrição necessária para o número de processos indicado. Os benefícios são estimativas indicativas baseadas em referências.",
    back: "Voltar",
    next: "Seguinte",
    calculate: "Calcular ROI",
    requestDemo: "Pedir uma demonstração",
  },
};

export function getRoiLanguage(language: string): RoiLanguage {
  const code = language.toLowerCase().split("-")[0];
  return Object.prototype.hasOwnProperty.call(translations, code) ? code as RoiLanguage : "en";
}

export function getRoiCopy(language: string, messages?: Translations): RoiCopy {
  const fallback = translations[getRoiLanguage(language)];
  if (!messages) return fallback;

  const translated = (path: string, value: string) => {
    const key = `roi.${path}`;
    const message = getTranslation(messages, key);
    return message === key ? value : message;
  };

  return {
    ...fallback,
    ...Object.fromEntries(Object.entries(fallback)
      .filter(([, value]) => typeof value === "string")
      .map(([key, value]) => [key, translated(key, value as string)])),
    steps: fallback.steps.map((step, index) => translated(`steps.${index}`, step)),
    sectors: Object.fromEntries(Object.entries(fallback.sectors)
      .map(([sector, label]) => [sector, translated(`sectors.${sector}`, label)])) as Record<Sector, string>,
  };
}
